import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import voiceService from "../services/voiceService";
import config from "../utils/config";

const VoiceContext = createContext();

export const useVoice = () => {
  const context = useContext(VoiceContext);
  if (!context) {
    throw new Error("useVoice must be used within a VoiceProvider");
  }
  return context;
};

export const VoiceProvider = ({ children }) => {
  const [device, setDevice] = useState(null);
  const [status, setStatus] = useState("disconnected"); // disconnected, connecting, ready, dialing, calling, ringing, error, disabled, unsupported
  const [activeCall, setActiveCall] = useState(null);
  const [currentNumber, setCurrentNumber] = useState(null);
  const [error, setError] = useState(null);
  const connectPromiseRef = useRef(null);

  // Initialize the device with a token. Returns a Promise that resolves when the device is ready.
  const initializeDevice = useCallback((token) => {
    return new Promise((resolve, reject) => {
      if (!token) {
        reject(new Error("Token is required to initialize device"));
        return;
      }

      try {
        console.log("VoiceContext: Initializing Twilio Device...");
        setStatus("connecting");
        const newDevice = voiceService.initialize(token);

        if (!newDevice) {
          console.error("VoiceContext: voiceService.initialize returned null");
          setStatus("error");
          reject(new Error("Failed to create device"));
          return;
        }

        let settled = false;
        const markReady = (source) => {
          if (settled) return;
          settled = true;
          console.log(`VoiceContext: Twilio Device is ready (${source})`);
          setStatus("ready");
          setDevice(newDevice);
          resolve(newDevice);
        };

        const onError = (err) => {
          const normalizedError =
            err instanceof Error
              ? err
              : new Error(err?.message || "Twilio device error");
          console.error("VoiceContext: Twilio Device error:", normalizedError);
          setStatus("error");
          setError(normalizedError);
          if (!settled) {
            settled = true;
            reject(normalizedError);
          }
        };

        // Set up event listeners
        // Twilio Voice SDK v2 uses "registered" as the reliable readiness signal.
        // Some environments may still emit "ready", so we support both.
        newDevice.on("ready", () => markReady("ready"));
        newDevice.on("registered", () => {
          console.log("VoiceContext: Twilio Device registered successfully");
          markReady("registered");
        });

        newDevice.on("error", onError);

        newDevice.on("unregistered", () => {
          console.log("VoiceContext: Twilio Device unregistered");
          setStatus("disconnected");
        });

        // Handle incoming connections or global connection events
        newDevice.on("connect", (conn) => {
          console.log("VoiceContext: Call connected (global listener)");
          setStatus("calling");
          setActiveCall(conn);

          conn.on("disconnect", () => {
            console.log("VoiceContext: Call disconnected (global listener)");
            setStatus("ready");
            setActiveCall(null);
            setCurrentNumber(null);
          });
        });

        // Handle device going offline (replaced 'disconnect' with 'offline' for SDK v2)
        newDevice.on("offline", () => {
          console.log("Device offline");
          setStatus("disconnected");
          setActiveCall(null);
          setCurrentNumber(null);
          setDevice(null);
        });

        newDevice.on("incoming", (conn) => {
          console.log("Incoming call from:", conn.parameters.From);
          setCurrentNumber(conn.parameters.From);
          conn.on("accept", () => {
            setStatus("calling");
            setActiveCall(conn);
          });
          conn.on("disconnect", () => {
            setStatus("ready");
            setActiveCall(null);
            setCurrentNumber(null);
          });
          conn.on("reject", () => {
            setStatus("ready");
            setActiveCall(null);
            setCurrentNumber(null);
          });
        });
      } catch (err) {
        console.error("Failed to initialize device:", err);
        setStatus("error");
        setError(err);
        reject(err instanceof Error ? err : new Error(err?.message || "Failed to initialize device"));
      }
    });
  }, []);

  // Connect (make a call)
  const makeCall = useCallback(
    async (options) => {
      try {
        const params = options?.params ?? options ?? {};
        console.log("VoiceContext: makeCall() triggered", {
          options,
          params,
          currentStatus: status,
        });

        if (status === "disabled" || status === "unsupported") {
          throw new Error("Voice unavailable");
        }

        // If not ready, connect first and wait until the device is actually ready
        let readyToCall = status === "ready";
        if (status === "disconnected" || status === "error") {
          console.log(
            "VoiceContext: Device not ready, connecting and waiting for ready...",
          );
          await connect();
          readyToCall = true; // connect() resolved only when device is ready
        }

        if (status === "connecting" && !readyToCall) {
          // If a connect is already in-flight (e.g. auto-connect), wait for it.
          await connect();
          readyToCall = true;
        }

        if (
          status === "dialing" ||
          status === "ringing" ||
          status === "calling"
        ) {
          console.warn("VoiceContext: Already in an active call state");
          return;
        }

        if (!readyToCall && status !== "ready") {
          throw new Error(`Device not ready (Current status: ${status})`);
        }

        setStatus("dialing");
        setCurrentNumber(params?.To);
        try {
          console.log("VoiceContext: Initiating connection via SDK...");
          // Connect returns a Call object (Promise resolving to it)
          const connection = await voiceService.connect(
            options?.params ? options : { params },
          );

          // Attach params to the connection object for UI display
          connection.outgoingParams = params;

          setActiveCall(connection);

          // Attach lifecycle listeners specifically for this outgoing call
          connection.on("ringing", () => {
            console.log("Outgoing call ringing...");
            setStatus("ringing");
          });

          connection.on("accept", () => {
            console.log("Outgoing call accepted");
            setStatus("calling");
          });

          connection.on("disconnect", () => {
            console.log("Outgoing call disconnected");
            setStatus("ready");
            setActiveCall(null);
            setCurrentNumber(null);
          });

          connection.on("error", (err) => {
            const normalizedError =
              err instanceof Error
                ? err
                : new Error(err?.message || "Call error");
          
            console.error("Call error:", normalizedError);
          
            setError(normalizedError);
          });

          return connection;
        } catch (connErr) {
          console.error("Connection failed immediately:", connErr);
          setStatus("ready");
          setActiveCall(null);
          setCurrentNumber(null);
          throw connErr;
        }
      } catch (err) {
        console.error("Error making call:", err);
      
        const normalizedError =
          err instanceof Error
            ? err
            : new Error(
                typeof err === "string"
                  ? err
                  : err?.message || "Voice call failed unexpectedly"
              );
      
        setError(normalizedError);
        throw normalizedError;
      }
    },
    [status],
  );

  // Disconnect active call
  const endCall = useCallback(() => {
    if (activeCall) {
      activeCall.disconnect();
    } else {
      voiceService.disconnectAll();
    }
    // State cleanup happens in event listeners
  }, [activeCall]);

  // Connect (fetch token and initialize)
  const connect = useCallback(async () => {
    console.log("VoiceContext: connect() called. Current status:", status);
    if (status === "disabled" || status === "unsupported") {
      console.warn(
        "VoiceContext: Connection aborted - voice is disabled or unsupported.",
      );
      return;
    }
    if (status === "ready") {
      console.log(
        "VoiceContext: Already ready or connecting, skipping fetchToken.",
      );
      return;
    }
    if (status === "connecting") {
      if (connectPromiseRef.current) {
        console.log("VoiceContext: Connection already in progress, awaiting...");
        return await connectPromiseRef.current;
      }
      // Fallback: if state says connecting but no promise is tracked, wait briefly.
      return;
    }

    try {
      connectPromiseRef.current = (async () => {
        console.log("VoiceContext: Fetching Twilio token...");
        const token = await voiceService.fetchToken();
        console.log(
          "VoiceContext: Token fetched successfully. Initializing device...",
        );
        await initializeDevice(token);
      })();
      await connectPromiseRef.current;
    } catch (err) {
      const normalizedError =
        err instanceof Error
          ? err
          : new Error(err?.message || "Voice connection failed");

      if (normalizedError.message === "Voice feature is disabled.") {
        setStatus("disabled");
      } else {
        console.error("Error connecting to voice service:", normalizedError);
        setStatus("error");
        setError(normalizedError);
      }
      throw normalizedError; // So callers (e.g. makeCall) know connection failed
    } finally {
      connectPromiseRef.current = null;
    }
  }, [initializeDevice, status]);

  // Check for support and enablement on mount, and auto-connect for agents
  useEffect(() => {
    const isEnabled = config.features.enableVoice;
    console.log("VoiceContext: Configuration Check", {
      isEnabled,
      rawEnv: process.env.REACT_APP_ENABLE_VOICE,
      type: typeof process.env.REACT_APP_ENABLE_VOICE,
    });

    if (!isEnabled) {
      console.warn(
        "VoiceContext: Voice feature is DISABLED because REACT_APP_ENABLE_VOICE is not 'true'. Current value:",
        process.env.REACT_APP_ENABLE_VOICE,
      );
      setStatus("disabled");
      return;
    }
    if (!voiceService.isBrowserSupported()) {
      console.warn("VoiceContext: Browser not supported for Twilio Voice.");
      setStatus("unsupported");
      return;
    }

    // Auto-connect if the user is already logged in as an agent
    const role = sessionStorage.getItem("userRole");
    if (role === "Agent" && status === "disconnected") {
      console.log("VoiceContext: Agent detected, triggering connect...");
      connect().catch((err) =>
        console.warn("VoiceContext: Auto-connect failed", err)
      );
    }
  }, [connect, status]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      voiceService.destroy();
    };
  }, []);

  const value = {
    device,
    status,
    activeCall,
    currentNumber,
    error,
    connect,
    initializeDevice,
    makeCall,
    endCall,
  };

  return (
    <VoiceContext.Provider value={value}>{children}</VoiceContext.Provider>
  );
};
